import React from 'react';
import { Link } from 'react-router-dom';

import RecordBadge from '../common/RecordBadge';
import DialogLoadState from '../common/DialogLoadState';
import { Skeleton } from '../loading';
import useTagReasons from '../../hooks/useTagReasons';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { formatDisplayDate } from '../../utils/dateDisplay';
import { normalizeId } from '../../utils/ids';
import { describeFact } from '../../utils/tagRules';

/**
 * "WHY" — the pop-over on an automatically applied tag chip (docs/plans/
 * tags.md §3.4 "Why", §5.1: "The pop-over gives the reason and, for
 * managers, 'Applied by a rule · Edit rule'"). What the rule found for THIS
 * item, from `GET /api/items/{id}/tag-reasons` (hooks/useTagReasons), in
 * plain sentences (utils/tagRules.js describeFact):
 *
 *   Applied by a rule · since 2 Mar 2026
 *   Latest Oil change is older than 180 days (last: 2 Mar 2026).  [Oil change · Spring service]
 *
 * - A related item — the latest one, or the one holding the smallest or
 *   largest value — appears as a record badge ONLY when the API names it,
 *   which it does only when the reader may read it. Otherwise the fact keeps
 *   its value and names no item. The API names its own collection and card
 *   (keepr-api T6b): the badge wears that card's tile and links into that
 *   collection — a related item may live in a sub-collection, or be of a card
 *   beneath the clause's.
 * - With `match: 'any'`, only the parts that hold are listed ("At least one
 *   of these holds"). A rule with no parts tags every item of its card.
 * - `canEditRule` (a manager of the tag's collection) adds **Edit rule**,
 *   the door to Settings › Tags with the rule's editor open.
 *
 * The rule itself is never shown: only managers read it (GET …/rule).
 */
export default function TagReasons({
	itemId,
	version = null,
	tagId,
	canEditRule = false,
	ruleCollectionId = null,
}) {
	const { reasons, loading, error, reload } = useTagReasons(itemId, version);
	const cards = useOptionalCardDefinitions();
	const cardsById = cards?.cardsById || {};
	const elementsFor = cardId => (typeof cards?.getElementsForCard === 'function' ? (cards.getElementsForCard(cardId) || []) : (cardsById[cardId]?.elements || []));
	const reason = reasons.find(entry => normalizeId(entry?.tagId) === normalizeId(tagId)) || null;

	const editLink = canEditRule && ruleCollectionId ? (
		<Link className="tag-reasons__edit" to={`/collections/${ruleCollectionId}/form?tab=tags&rule=${normalizeId(tagId)}`}>
			Edit rule
		</Link>
	) : null;

	let body;
	if (loading || error) {
		body = (
			<DialogLoadState
				loaded={false}
				loading={loading}
				error={Boolean(error)}
				label="why this tag is here"
				onRetry={reload}
				skeleton={<Skeleton variant="line" count={2} />}
				minHeight={72}
			/>
		);
	} else if (!reason) {
		body = <p className="tag-reasons__lead">A rule applies this tag. What it found is not known yet: it may still be applying.</p>;
	} else {
		const facts = reason.facts || {};
		const any = facts.match === 'any';
		const related = Array.isArray(facts.related) ? facts.related : [];
		const parts = (facts.where === true || facts.where === false ? 1 : 0) + related.length;
		const lines = [];
		if (facts.where === true || (facts.where === false && !any)) {
			lines.push({ key: 'where', text: facts.where ? 'It matches the rule’s filter.' : 'It does not match the rule’s filter.' });
		}
		related.forEach((fact, index) => {
			if (any && !fact.holds) return;
			const cardId = normalizeId(fact.card_id);
			const card = cardsById[cardId] || null;
			const named = fact.item && fact.item.item_id ? fact.item : null;
			lines.push({
				key: `related-${fact.i ?? index}`,
				text: describeFact(fact, { card, elements: elementsFor(cardId) }),
				item: named ? {
					...named,
					card: cardsById[normalizeId(named.card_id)] || null,
					collectionId: normalizeId(named.collection_id),
				} : null,
			});
		});
		let lead = null;
		if (!parts) lead = 'Every item of its card gets this tag.';
		else if (parts > 1) lead = any ? 'At least one of these holds:' : 'All of these hold:';
		body = (
			<>
				{lead ? <p className="tag-reasons__lead">{lead}</p> : null}
				{lines.length ? (
					<ul className="tag-reasons__facts">
						{lines.map(line => (
							<li key={line.key} className="tag-reasons__fact">
								<span>{line.text}</span>
								{line.item ? (
									<RecordBadge
										kind="item"
										size="xs"
										record={line.item.card}
										title={line.item.title || 'Untitled item'}
										to={line.item.collectionId ? `/collections/${line.item.collectionId}/items/${normalizeId(line.item.item_id)}` : undefined}
										className="tag-reasons__item"
									/>
								) : null}
							</li>
						))}
					</ul>
				) : null}
			</>
		);
	}

	return (
		<div className="tag-reasons" data-testid="tag-reasons">
			{reason?.since ? <p className="tag-reasons__since">{`Since ${formatDisplayDate(reason.since)}`}</p> : null}
			{body}
			{editLink}
		</div>
	);
}
